import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Ruler, Play, Square } from 'lucide-react';
import { NoteEvent, PitchContourPoint, AnalysisData } from '../types';
import { harmonicColor } from '../utils';

// Singing vowel templates from Csound formant data per voice type
// Italian vowels (a, e, i, o, u) — the standard classical singing set
export type VoiceType = 'bass' | 'tenor' | 'countertenor' | 'alto' | 'soprano';

export const SINGING_VOWELS: Record<VoiceType, { ipa: string; f1: number; f2: number }[]> = {
  bass: [
    { ipa: 'a', f1: 600, f2: 1040 },
    { ipa: 'e', f1: 400, f2: 1620 },
    { ipa: 'i', f1: 250, f2: 1750 },
    { ipa: 'o', f1: 400, f2: 750 },
    { ipa: 'u', f1: 350, f2: 600 },
  ],
  tenor: [
    { ipa: 'a', f1: 650, f2: 1080 },
    { ipa: 'e', f1: 400, f2: 1700 },
    { ipa: 'i', f1: 290, f2: 1870 },
    { ipa: 'o', f1: 400, f2: 800 },
    { ipa: 'u', f1: 350, f2: 600 },
  ],
  countertenor: [
    { ipa: 'a', f1: 660, f2: 1120 },
    { ipa: 'e', f1: 440, f2: 1800 },
    { ipa: 'i', f1: 270, f2: 1850 },
    { ipa: 'o', f1: 430, f2: 820 },
    { ipa: 'u', f1: 370, f2: 630 },
  ],
  alto: [
    { ipa: 'a', f1: 800, f2: 1150 },
    { ipa: 'e', f1: 400, f2: 1600 },
    { ipa: 'i', f1: 350, f2: 1700 },
    { ipa: 'o', f1: 450, f2: 800 },
    { ipa: 'u', f1: 325, f2: 700 },
  ],
  soprano: [
    { ipa: 'a', f1: 800, f2: 1150 },
    { ipa: 'e', f1: 350, f2: 2000 },
    { ipa: 'i', f1: 270, f2: 2140 },
    { ipa: 'o', f1: 450, f2: 800 },
    { ipa: 'u', f1: 325, f2: 700 },
  ],
};

// F1/F2 range for the chart
const F1_MIN = 200, F1_MAX = 900;
const F2_MIN = 500, F2_MAX = 2200;

export function VowelChart({
  formantPos, onFormantChange, showRulers, onToggleRulers, selectedNote, allNotes, spectrogram, pitchContour, audioUrl, onSynthProfileChange, currentTime, onMuteAudio, isAudioPlaying, onPraatFormants
}: {
  formantPos: { f1: number; f2: number } | null;
  onFormantChange: (pos: { f1: number; f2: number } | null) => void;
  showRulers: boolean;
  onToggleRulers: () => void;
  selectedNote?: NoteEvent | null;
  allNotes?: NoteEvent[];
  spectrogram?: AnalysisData['spectrogram'];
  pitchContour?: PitchContourPoint[];
  audioUrl?: string;
  onSynthProfileChange?: (profile: { freq: number; db: number; bw?: number }[] | null) => void;
  currentTime?: number;
  onMuteAudio?: (mute: boolean) => void;
  isAudioPlaying?: boolean;
  onPraatFormants?: (formants: Record<string, { freq: number | null; bw: number | null }> | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [voiceType, setVoiceType] = useState<VoiceType>('tenor');
  const [activeTab, setActiveTab] = useState<'vowel' | 'analysis' | 'synth'>('vowel');
  const isDragging = useRef(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const vowels = SINGING_VOWELS[voiceType];
  const pad = 30;
  const topPad = 50;

  const getPlotDims = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return { W: 0, H: 0, plotW: 0, plotH: 0 };
    const rect = canvas.getBoundingClientRect();
    return { W: rect.width, H: rect.height, plotW: rect.width - pad * 2, plotH: rect.height - topPad - pad };
  }, []);

  const toX = useCallback((f2: number) => {
    const { plotW } = getPlotDims();
    return pad + (1 - (f2 - F2_MIN) / (F2_MAX - F2_MIN)) * plotW;
  }, [getPlotDims]);
  const toY = useCallback((f1: number) => {
    const { plotH } = getPlotDims();
    return topPad + (f1 - F1_MIN) / (F1_MAX - F1_MIN) * plotH;
  }, [getPlotDims]);
  const fromX = useCallback((x: number) => {
    const { plotW } = getPlotDims();
    return F2_MIN + (1 - (x - pad) / plotW) * (F2_MAX - F2_MIN);
  }, [getPlotDims]);
  const fromY = useCallback((y: number) => {
    const { plotH } = getPlotDims();
    return F1_MIN + (y - topPad) / plotH * (F1_MAX - F1_MIN);
  }, [getPlotDims]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return; // hidden — don't clear buffer
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    const W = rect.width;
    const H = rect.height;
    const plotW = W - pad * 2;
    const plotH = H - topPad - pad;

    const txf = (f2: number) => pad + (1 - (f2 - F2_MIN) / (F2_MAX - F2_MIN)) * plotW;
    const tyf = (f1: number) => topPad + (f1 - F1_MIN) / (F1_MAX - F1_MIN) * plotH;

    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (const f2 of [2000, 1600, 1200, 800]) {
      const x = txf(f2);
      if (x < pad || x > W - pad) continue;
      ctx.fillText(`${f2}`, x, topPad - 6);
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(x, topPad); ctx.lineTo(x, H - pad); ctx.stroke();
    }
    ctx.textAlign = 'right';
    for (const f1 of [300, 400, 500, 600, 700, 800]) {
      const y = tyf(f1);
      if (y < topPad || y > H - pad) continue;
      ctx.fillText(`${f1}`, pad - 4, y + 3);
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(W - pad, y); ctx.stroke();
    }

    // Vowel targets — clickable
    ctx.font = '14px serif';
    ctx.textAlign = 'center';
    vowels.forEach(v => {
      const x = txf(v.f2);
      const y = tyf(v.f1);
      if (x < pad || x > W - pad || y < topPad || y > H - pad) return;
      ctx.beginPath();
      ctx.arc(x, y, 12, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(100, 100, 140, 0.15)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(100, 100, 140, 0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#777';
      ctx.fillText(v.ipa, x, y + 5);
    });

    // Draggable indicator
    if (formantPos) {
      const ix = txf(formantPos.f2);
      const iy = tyf(formantPos.f1);

      // Crosshair lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 0.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(ix, topPad); ctx.lineTo(ix, H - pad); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(pad, iy); ctx.lineTo(W - pad, iy); ctx.stroke();
      ctx.setLineDash([]);

      // Glow
      ctx.beginPath();
      ctx.arc(ix, iy, 12, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(96, 165, 250, 0.15)';
      ctx.fill();

      // Dot
      ctx.beginPath();
      ctx.arc(ix, iy, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.strokeStyle = '#60a5fa';
      ctx.lineWidth = 2;
      ctx.stroke();

      // F1/F2 readout
      ctx.fillStyle = '#60a5fa';
      ctx.font = '10px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`F1: ${Math.round(formantPos.f1)} Hz`, pad + 4, H - pad + 14);
      ctx.textAlign = 'right';
      ctx.fillText(`F2: ${Math.round(formantPos.f2)} Hz`, W - pad - 4, H - pad + 14);
    }
  }, [vowels, formantPos]);

  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => { draw(); }, [draw]);
  // Force redraw when tab becomes visible (canvas goes from display:none to block)
  useEffect(() => {
    if (activeTab === 'vowel') {
      requestAnimationFrame(() => requestAnimationFrame(() => drawRef.current()));
    }
  }, [activeTab]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  // During playback, update formants based on which note currentTime falls in
  const lastFormantNoteRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentTime == null || !allNotes || allNotes.length === 0) return;
    const note = allNotes.find(n => currentTime >= n.start_s && currentTime <= n.end_s);
    if (!note) return;
    const key = praatCacheKey(note);
    if (key === lastFormantNoteRef.current) return; // same note, no update
    lastFormantNoteRef.current = key;
    const cached = praatCache.get(key);
    if (!cached?.formants) return;
    const f = cached.formants;
    if (f.F1?.freq != null && f.F2?.freq != null) {
      onFormantChange({ f1: f.F1.freq, f2: f.F2.freq });
    }
    onPraatFormants?.(f);
  }, [currentTime, allNotes, onFormantChange, onPraatFormants]);

  const posFromEvent = useCallback((e: React.MouseEvent) => {
    const f1 = fromY(e.clientY - (canvasRef.current?.getBoundingClientRect().top || 0));
    const f2 = fromX(e.clientX - (canvasRef.current?.getBoundingClientRect().left || 0));
    return {
      f1: Math.max(F1_MIN, Math.min(F1_MAX, f1)),
      f2: Math.max(F2_MIN, Math.min(F2_MAX, f2)),
    };
  }, [fromX, fromY]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Check if clicking a vowel label
    for (const v of vowels) {
      const vx = toX(v.f2);
      const vy = toY(v.f1);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) continue;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      if (Math.sqrt((mx - vx) ** 2 + (my - vy) ** 2) < 15) {
        onFormantChange({ f1: v.f1, f2: v.f2 });
        return;
      }
    }
    // Start drag
    isDragging.current = true;
    onFormantChange(posFromEvent(e));
  }, [vowels, toX, toY, posFromEvent, onFormantChange]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    onFormantChange(posFromEvent(e));
  }, [posFromEvent, onFormantChange]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  return (
    <div className="vowel-chart-container">
      <div className="vowel-chart-header">
        <div className="vowel-tabs">
          <button className={`vowel-tab ${activeTab === 'vowel' ? 'active' : ''}`} onClick={() => setActiveTab('vowel')}>Vowel</button>
          <button className={`vowel-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')}>Analysis</button>
          <button className={`vowel-tab ${activeTab === 'synth' ? 'active' : ''}`} onClick={() => setActiveTab('synth')}>Synth</button>
        </div>
        {activeTab === 'vowel' && (
          <div className="vowel-chart-controls">
          <div className="vt-dropdown" onClick={() => setDropdownOpen(!dropdownOpen)}>
            <span className="vt-dropdown-label">{voiceType}</span>
            <span className="vt-dropdown-arrow">{dropdownOpen ? '▲' : '▼'}</span>
            {dropdownOpen && (
              <div className="vt-dropdown-menu">
                {(['bass', 'tenor', 'countertenor', 'alto', 'soprano'] as VoiceType[]).map(vt => (
                  <div key={vt}
                    className={`vt-dropdown-item ${voiceType === vt ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setVoiceType(vt); setDropdownOpen(false); }}>
                    {vt}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className={`ruler-toggle-btn ${showRulers ? 'active' : ''}`}
            onClick={onToggleRulers}
            title={showRulers ? 'Hide rulers' : 'Show rulers'}>
            <Ruler size={12} />
          </button>
          </div>
        )}
      </div>
      <canvas ref={canvasRef} className="vowel-chart-canvas"
        onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        style={{ cursor: 'crosshair', display: activeTab === 'vowel' ? 'block' : 'none' }} />
      {activeTab === 'analysis' && (
        <AnalysisPanel selectedNote={selectedNote} allNotes={allNotes} pitchContour={pitchContour} audioUrl={audioUrl} onFormantChange={onFormantChange} onPraatFormants={onPraatFormants} />
      )}
      {activeTab === 'synth' && (
        <SynthTab selectedNote={selectedNote} spectrogram={spectrogram} onSynthProfileChange={onSynthProfileChange} currentTime={currentTime} onMuteAudio={onMuteAudio} isAudioPlaying={isAudioPlaying} />
      )}
    </div>
  );
}

// ── Analysis Panel ──────────────────────

// Cache Praat results by note key so navigating between notes preserves results
const praatCache = new Map<string, any>();
function praatCacheKey(note: NoteEvent): string {
  return `${note.start_s.toFixed(3)}_${note.end_s.toFixed(3)}_${note.note}`;
}

// Encode a Float32Array chunk as WAV blob
function encodeWavChunk(chan: Float32Array, start: number, end: number, sr: number): Blob {
  const startSamp = Math.max(0, Math.floor(start * sr));
  const endSamp = Math.min(chan.length, Math.ceil(end * sr));
  const chunk = chan.slice(startSamp, endSamp);
  const buf = new ArrayBuffer(44 + chunk.length * 2);
  const v = new DataView(buf);
  const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, 36 + chunk.length * 2, true); ws(8, 'WAVE');
  ws(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
  v.setUint16(22, 1, true); v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  ws(36, 'data'); v.setUint32(40, chunk.length * 2, true);
  for (let i = 0; i < chunk.length; i++) {
    const s = Math.max(-1, Math.min(1, chunk[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

// Send a WAV blob to the Praat API
async function fetchPraat(wavBlob: Blob): Promise<any> {
  const formData = new FormData();
  formData.append('audio', wavBlob, 'chunk.wav');
  const resp = await fetch('/api/praat', { method: 'POST', body: formData });
  if (!resp.ok) throw new Error('Server error');
  const data = await resp.json();
  if (data.error) throw new Error(data.error);
  return data;
}

function AnalysisPanel({ selectedNote, allNotes, pitchContour, audioUrl, onFormantChange, onPraatFormants }: {
  selectedNote?: NoteEvent | null;
  allNotes?: NoteEvent[];
  pitchContour?: PitchContourPoint[];
  audioUrl?: string;
  onFormantChange?: (pos: { f1: number; f2: number } | null) => void;
  onPraatFormants?: (formants: Record<string, { freq: number | null; bw: number | null }> | null) => void;
}) {
  const contourCanvasRef = useRef<HTMLCanvasElement>(null);
  const [praatResult, setPraatResult] = useState<any>(null);
  const [praatLoading, setPraatLoading] = useState(false);
  const [praatError, setPraatError] = useState<string | null>(null);
  const [analyzeAllProgress, setAnalyzeAllProgress] = useState<string | null>(null);

  // Load cached result when note changes
  useEffect(() => {
    if (selectedNote) {
      const cached = praatCache.get(praatCacheKey(selectedNote));
      setPraatResult(cached || null);
    } else {
      setPraatResult(null);
    }
    setPraatError(null);
  }, [selectedNote]);

  // Auto-update vowel chart F1/F2 and bubble up formant data when Praat results arrive
  useEffect(() => {
    if (!praatResult?.formants) {
      onPraatFormants?.(null);
      return;
    }
    const f = praatResult.formants;
    if (f.F1?.freq != null && f.F2?.freq != null) {
      onFormantChange?.({ f1: f.F1.freq, f2: f.F2.freq });
    }
    onPraatFormants?.(f);
  }, [praatResult]);

  // Decode audio once, reuse for all notes
  const decodeAudio = useCallback(async (): Promise<{ chan: Float32Array; sr: number }> => {
    if (!audioUrl) throw new Error('No audio');
    const resp = await fetch(audioUrl);
    const arrayBuf = await resp.arrayBuffer();
    const ctx = new AudioContext();
    const audioBuf = await ctx.decodeAudioData(arrayBuf);
    const sr = audioBuf.sampleRate;
    const chan = audioBuf.getChannelData(0);
    ctx.close();
    return { chan, sr };
  }, [audioUrl]);

  const runPraatAnalysis = useCallback(async () => {
    if (!selectedNote || !audioUrl) return;
    setPraatLoading(true);
    setPraatError(null);
    try {
      const { chan, sr } = await decodeAudio();
      const wavBlob = encodeWavChunk(chan, selectedNote.start_s, selectedNote.end_s, sr);
      const data = await fetchPraat(wavBlob);
      setPraatResult(data);
      praatCache.set(praatCacheKey(selectedNote), data);
    } catch (e: any) {
      setPraatError(e.message || 'Failed');
    } finally {
      setPraatLoading(false);
    }
  }, [selectedNote, audioUrl, decodeAudio]);

  const runPraatAll = useCallback(async () => {
    if (!allNotes || allNotes.length === 0 || !audioUrl) return;
    // Skip notes already cached
    const uncached = allNotes.filter(n => !praatCache.has(praatCacheKey(n)));
    if (uncached.length === 0) {
      // All cached — just load current
      if (selectedNote) setPraatResult(praatCache.get(praatCacheKey(selectedNote)) || null);
      return;
    }
    setPraatLoading(true);
    setPraatError(null);
    try {
      const { chan, sr } = await decodeAudio();
      // Process in batches of 4 (parallel but not overwhelming)
      for (let i = 0; i < uncached.length; i += 4) {
        const batch = uncached.slice(i, i + 4);
        setAnalyzeAllProgress(`${i + 1}/${uncached.length}`);
        const results = await Promise.all(
          batch.map(async (note) => {
            try {
              const wavBlob = encodeWavChunk(chan, note.start_s, note.end_s, sr);
              return { note, data: await fetchPraat(wavBlob) };
            } catch {
              return { note, data: null };
            }
          })
        );
        for (const { note, data } of results) {
          if (data) praatCache.set(praatCacheKey(note), data);
        }
      }
      // Load current note's result
      if (selectedNote) setPraatResult(praatCache.get(praatCacheKey(selectedNote)) || null);
    } catch (e: any) {
      setPraatError(e.message || 'Failed');
    } finally {
      setPraatLoading(false);
      setAnalyzeAllProgress(null);
    }
  }, [allNotes, selectedNote, audioUrl, decodeAudio]);

  // Extract pitch contour points within this note
  const noteContour = useMemo(() => {
    if (!selectedNote || !pitchContour) return [];
    return pitchContour.filter(
      p => p.t >= selectedNote.start_s - 0.01 && p.t <= selectedNote.end_s + 0.01 && p.f0 > 0
    );
  }, [selectedNote, pitchContour]);

  // Draw mini pitch contour
  useEffect(() => {
    const canvas = contourCanvasRef.current;
    if (!canvas || noteContour.length < 2) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    const W = rect.width, H = rect.height;
    const pad = 4;

    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, W, H);

    const midis = noteContour.map(p => 69 + 12 * Math.log2(p.f0 / 440));
    const tMin = noteContour[0].t;
    const tMax = noteContour[noteContour.length - 1].t;
    const tRange = Math.max(0.01, tMax - tMin);
    const midiMin = Math.min(...midis) - 0.3;
    const midiMax = Math.max(...midis) + 0.3;
    const midiRange = Math.max(0.5, midiMax - midiMin);

    // Target pitch line (nearest semitone)
    const medianMidi = midis.slice().sort((a, b) => a - b)[Math.floor(midis.length / 2)];
    const targetMidi = Math.round(medianMidi);
    const targetY = pad + (H - pad * 2) * (1 - (targetMidi - midiMin) / midiRange);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(pad, targetY);
    ctx.lineTo(W - pad, targetY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Downsample to ~1 point per pixel, then draw with quadratic curves
    const pixelDt = tRange / (W - pad * 2);
    const pts: { x: number; y: number }[] = [];
    let bucketT = -1, bucketMidi = 0, bucketN = 0;
    for (let i = 0; i < noteContour.length; i++) {
      if (bucketN === 0 || (noteContour[i].t - bucketT) >= pixelDt) {
        if (bucketN > 0) {
          const avgMidi = bucketMidi / bucketN;
          pts.push({
            x: pad + ((bucketT - tMin) / tRange) * (W - pad * 2),
            y: pad + (H - pad * 2) * (1 - (avgMidi - midiMin) / midiRange),
          });
        }
        bucketT = noteContour[i].t; bucketMidi = midis[i]; bucketN = 1;
      } else {
        bucketMidi += midis[i]; bucketN++;
      }
    }
    if (bucketN > 0) {
      pts.push({
        x: pad + ((bucketT - tMin) / tRange) * (W - pad * 2),
        y: pad + (H - pad * 2) * (1 - (bucketMidi / bucketN - midiMin) / midiRange),
      });
    }

    if (pts.length >= 2) {
      ctx.beginPath();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 0; i < pts.length - 1; i++) {
        const cx = (pts[i].x + pts[i + 1].x) / 2;
        const cy = (pts[i].y + pts[i + 1].y) / 2;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, cx, cy);
      }
      ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
      ctx.strokeStyle = '#60a5fa';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [noteContour]);

  if (!selectedNote) {
    return <div className="analysis-tab-content"><span className="analysis-tab-placeholder">Select a note to analyze</span></div>;
  }

  const f0 = selectedNote.f0_median_hz || 0;
  const midi = f0 > 0 ? 69 + 12 * Math.log2(f0 / 440) : 0;
  const cents = Math.round((midi - Math.round(midi)) * 100);
  const centsStr = cents >= 0 ? `+${cents}` : `${cents}`;
  const centsColor = Math.abs(cents) <= 10 ? '#22c55e' : Math.abs(cents) <= 25 ? '#eab308' : '#f87171';

  const jitter = selectedNote.jitter_median_pct;
  const jitterColor = jitter == null ? '#666' : jitter < 1 ? '#22c55e' : jitter < 2 ? '#eab308' : '#f87171';

  const shimmer = selectedNote.shimmer_median_pct;
  const shimmerColor = shimmer == null ? '#666' : shimmer < 3 ? '#22c55e' : shimmer < 6 ? '#eab308' : '#f87171';

  const vibRate = (selectedNote as any).vibrato_rate_hz;
  const vibExtent = (selectedNote as any).vibrato_extent_cents;
  const hasVibrato = vibRate != null && vibRate > 0;
  const vibRateColor = !hasVibrato ? '#666' : (vibRate >= 4.5 && vibRate <= 7.5) ? '#22c55e' : '#eab308';

  const Metric = ({ label, value, unit, color }: { label: string; value: string; unit?: string; color?: string }) => (
    <div style={{ flex: 1, minWidth: 60 }}>
      <div style={{ fontSize: 9, color: '#71717a', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: color || '#ddd' }}>{value}<span style={{ fontSize: 10, color: '#555' }}>{unit || ''}</span></div>
    </div>
  );

  return (
    <div className="analysis-tab-content" style={{ flexDirection: 'column', alignItems: 'stretch', padding: '4px 12px', gap: 4, overflow: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{selectedNote.note}</span>
        <span style={{ fontSize: 10, color: '#71717a' }}>{(selectedNote.end_s - selectedNote.start_s).toFixed(2)}s</span>
      </div>

      <canvas ref={contourCanvasRef} style={{ width: '100%', height: 50, borderRadius: 4 }} />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Metric label="Tuning" value={centsStr} unit="¢" color={centsColor} />
        <Metric label="F0" value={f0.toFixed(0)} unit="Hz" />
        <Metric label="Vibrato" value={hasVibrato ? vibRate.toFixed(1) : '–'} unit={hasVibrato ? 'Hz' : ''} color={vibRateColor} />
        <Metric label="Extent" value={hasVibrato ? vibExtent.toFixed(0) : '–'} unit={hasVibrato ? '¢' : ''} />
      </div>

      {praatResult ? (<>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Metric label="Jitter" value={praatResult.jitter_local_pct != null ? praatResult.jitter_local_pct.toFixed(3) : '–'} unit="%"
            color={praatResult.jitter_local_pct == null ? '#666' : praatResult.jitter_local_pct < 1 ? '#22c55e' : praatResult.jitter_local_pct < 2 ? '#eab308' : '#f87171'} />
          <Metric label="Shimmer" value={praatResult.shimmer_local_pct != null ? praatResult.shimmer_local_pct.toFixed(3) : '–'} unit="%"
            color={praatResult.shimmer_local_pct == null ? '#666' : praatResult.shimmer_local_pct < 3 ? '#22c55e' : praatResult.shimmer_local_pct < 6 ? '#eab308' : '#f87171'} />
          <Metric label="HNR" value={praatResult.hnr_db != null ? praatResult.hnr_db.toFixed(1) : '–'} unit="dB"
            color={praatResult.hnr_db == null ? '#666' : praatResult.hnr_db > 20 ? '#22c55e' : praatResult.hnr_db > 10 ? '#eab308' : '#f87171'} />
          <Metric label="CPP" value={praatResult.cpp_db != null ? praatResult.cpp_db.toFixed(1) : '–'} unit="dB" />
        </div>
        {praatResult.formants && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['F1','F2','F3','F4','F5','F6'] as const).map(key => {
              const f = praatResult.formants[key];
              return <Metric key={key} label={key} value={f?.freq != null ? f.freq.toFixed(0) : '–'} unit="Hz" />;
            })}
          </div>
        )}
      </>) : (
        <button onClick={runPraatAll} disabled={praatLoading} style={{
          background: 'transparent', border: '1px solid #333', borderRadius: 4,
          padding: '4px 10px', color: '#a1a1aa', fontSize: 10, cursor: 'pointer',
          alignSelf: 'flex-start',
        }}>
          {praatLoading ? (analyzeAllProgress ? `Analyzing ${analyzeAllProgress}...` : 'Analyzing...') : praatError ? `Retry (${praatError})` : 'Detailed Analysis (Praat)'}
        </button>
      )}
    </div>
  );
}

// ── Synth Tab: editable harmonic synthesis ──────────────────────

type EditableHarmonic = { n: number; freq: number; db: number; bw: number };

function computeProfile(selectedNote: NoteEvent, spectrogram: AnalysisData['spectrogram']): { f0: number; profile: EditableHarmonic[] } | null {
  if (!spectrogram?.harmonics) return null;
  const noteHarmonics = spectrogram.harmonics.filter(
    h => h.t >= selectedNote.start_s - 0.05 && h.t <= selectedNote.end_s + 0.05 && h.peaks.length > 0
  );
  if (noteHarmonics.length === 0) return null;
  const maxN = Math.max(...noteHarmonics.flatMap(h => h.peaks.map(p => p.n)));
  const profile: EditableHarmonic[] = [];
  const f0 = selectedNote.f0_median_hz || noteHarmonics[0].f0;
  for (let n = 1; n <= maxN; n++) {
    const dbs: number[] = [], bws: number[] = [];
    for (const h of noteHarmonics) {
      const peak = h.peaks.find(p => p.n === n);
      if (peak && peak.db > -80) { dbs.push(peak.db); bws.push(peak.bw || 20); }
    }
    if (dbs.length > 0) {
      dbs.sort((a, b) => a - b); bws.sort((a, b) => a - b);
      profile.push({ n, freq: f0 * n, db: dbs[Math.floor(dbs.length / 2)], bw: bws[Math.floor(bws.length / 2)] });
    }
  }
  return { f0, profile };
}

// Live synth using a fixed pool of OscillatorNodes — updates in real-time
class LiveSynth {
  ctx: AudioContext;
  oscillators: { osc: OscillatorNode; gain: GainNode }[] = [];
  masterGain: GainNode;
  private maxOscillators: number;

  constructor(maxOsc: number = 40) {
    this.ctx = new AudioContext();
    this.maxOscillators = maxOsc;
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.3;
    this.masterGain.connect(this.ctx.destination);

    // Pre-allocate oscillator pool
    for (let i = 0; i < maxOsc; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 440;
      const gain = this.ctx.createGain();
      gain.gain.value = 0; // muted initially
      osc.connect(gain);
      gain.connect(this.masterGain);
      osc.start();
      this.oscillators.push({ osc, gain });
    }
  }

  update(profile: { freq: number; db: number }[]) {
    const maxDb = profile.length > 0 ? Math.max(...profile.map(p => p.db)) : 0;
    const t = this.ctx.currentTime;
    for (let i = 0; i < this.maxOscillators; i++) {
      if (i < profile.length) {
        this.oscillators[i].osc.frequency.setTargetAtTime(profile[i].freq, t, 0.02);
        this.oscillators[i].gain.gain.setTargetAtTime(
          Math.pow(10, (profile[i].db - maxDb) / 20), t, 0.02
        );
      } else {
        // Mute unused oscillators
        this.oscillators[i].gain.gain.setTargetAtTime(0, t, 0.01);
      }
    }
  }

  stop() {
    for (const o of this.oscillators) { o.osc.stop(); }
    this.ctx.close();
  }
}

function SynthTab({ selectedNote, spectrogram, onSynthProfileChange, currentTime, onMuteAudio, isAudioPlaying }: {
  selectedNote?: NoteEvent | null;
  spectrogram?: AnalysisData['spectrogram'];
  onSynthProfileChange?: (profile: { freq: number; db: number; bw?: number }[] | null) => void;
  currentTime?: number;
  onMuteAudio?: (mute: boolean) => void;
  isAudioPlaying?: boolean;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [synthMode, setSynthMode] = useState<'static' | 'follow'>('static');
  const [editedProfile, setEditedProfile] = useState<EditableHarmonic[] | null>(null);
  const [modalH, setModalH] = useState<number | null>(null);
  const [isEdited, setIsEdited] = useState(false);
  const synthRef = useRef<LiveSynth | null>(null);
  const dragRef = useRef<{ idx: number; startY: number; startDb: number } | null>(null);

  const measured = useMemo(() => {
    if (!selectedNote || !spectrogram) return null;
    return computeProfile(selectedNote, spectrogram);
  }, [selectedNote, spectrogram]);

  // Reset editable profile when note changes
  useEffect(() => {
    if (measured) { setEditedProfile(measured.profile.map(p => ({ ...p }))); setIsEdited(false); setModalH(null); }
    else { setEditedProfile(null); }
  }, [measured]);

  const profile = editedProfile;
  const f0 = measured?.f0 || 0;

  // In follow mode, show the harmonics at the current playhead position
  const followProfile = useMemo((): EditableHarmonic[] | null => {
    if (synthMode !== 'follow' || !spectrogram?.harmonics || currentTime == null) return null;
    const harmonics = spectrogram.harmonics;
    let best = harmonics[0], bestD = Infinity;
    for (const h of harmonics) {
      const d = Math.abs(h.t - currentTime);
      if (d < bestD) { best = h; bestD = d; }
    }
    if (bestD > 0.5 || !best?.peaks?.length) return null;
    return best.peaks.map(p => ({ n: p.n, freq: p.freq, db: p.db, bw: p.bw || 20 }));
  }, [synthMode, spectrogram, currentTime]);

  const displayProfile = (synthMode === 'follow' && followProfile) ? followProfile : profile;
  const displayF0 = (synthMode === 'follow' && followProfile && followProfile.length > 0)
    ? followProfile[0].freq
    : f0;

  const updateHarmonic = (idx: number, updates: Partial<EditableHarmonic>) => {
    if (!profile) return;
    const next = profile.map((p, i) => i === idx ? { ...p, ...updates } : p);
    setEditedProfile(next);
    setIsEdited(true);
    if (synthRef.current) { synthRef.current.update(next); onSynthProfileChange?.(next); }
  };

  const reset = () => {
    if (measured) { setEditedProfile(measured.profile.map(p => ({ ...p }))); setIsEdited(false); }
  };

  const play = () => {
    if (isPlaying) return;
    const maxHarmonics = spectrogram?.harmonics
      ? Math.max(40, ...spectrogram.harmonics.map(h => h.peaks.length))
      : 40;
    synthRef.current = new LiveSynth(maxHarmonics);
    if (synthMode === 'static' && profile) {
      synthRef.current.update(profile);
      onSynthProfileChange?.(profile);
    }
    if (synthMode === 'follow') onMuteAudio?.(true);
    setIsPlaying(true);
  };

  const stop = () => {
    synthRef.current?.stop();
    synthRef.current = null;
    setIsPlaying(false);
    onSynthProfileChange?.(null);
    onMuteAudio?.(false);
  };

  // Follow mode: update synth from spectrogram harmonics at currentTime
  const lastFollowTime = useRef(-1);
  useEffect(() => {
    if (!isPlaying || synthMode !== 'follow' || !synthRef.current || !spectrogram?.harmonics) return;
    const t = currentTime || 0;
    if (Math.abs(t - lastFollowTime.current) < 0.005) return; // skip if barely moved
    lastFollowTime.current = t;

    // Find nearest harmonic entry
    const harmonics = spectrogram.harmonics;
    let best = harmonics[0], bestD = Math.abs(harmonics[0].t - t);
    for (const h of harmonics) {
      const d = Math.abs(h.t - t);
      if (d < bestD) { best = h; bestD = d; }
    }
    if (bestD < 0.5 && best.peaks.length > 0) {
      synthRef.current.update(best.peaks);
      onSynthProfileChange?.(best.peaks);
    } else {
      // In a gap — mute
      synthRef.current.update([]);
      onSynthProfileChange?.(null);
    }
  }, [isPlaying, synthMode, currentTime, spectrogram]); // eslint-disable-line react-hooks/exhaustive-deps

  // In follow mode, stop synth when audio pauses
  useEffect(() => {
    if (isPlaying && synthMode === 'follow' && !isAudioPlaying) {
      stop();
    }
  }, [isAudioPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop synth when note changes or component unmounts
  useEffect(() => { return () => { synthRef.current?.stop(); onSynthProfileChange?.(null); onMuteAudio?.(false); }; }, [measured]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag amplitude
  const onBarMouseDown = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    dragRef.current = { idx, startY: e.clientY, startDb: profile![idx].db };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dy = ev.clientY - dragRef.current.startY;
      const dbChange = -(dy / 2); // 2px per dB
      updateHarmonic(dragRef.current.idx, { db: Math.min(0, dragRef.current.startDb + dbChange) });
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!selectedNote) {
    return <div className="analysis-tab-content"><span className="analysis-tab-placeholder">Select a note to analyze</span></div>;
  }
  if (!profile || !measured) {
    return <div className="analysis-tab-content"><span className="analysis-tab-placeholder">No harmonic data for this note</span></div>;
  }

  const dp = displayProfile || profile;
  const maxDb = dp && dp.length > 0 ? Math.max(...dp.map(p => p.db)) : 0;
  const dbRange = 80;
  const midi = 69 + 12 * Math.log2((displayF0 || f0 || 440) / 440);
  const cents = Math.round((midi - Math.round(midi)) * 100);
  const centsStr = cents >= 0 ? `+${cents}` : `${cents}`;
  const centsColor = Math.abs(cents) <= 10 ? '#22c55e' : Math.abs(cents) <= 25 ? '#eab308' : '#f87171';

  return (
    <div className="analysis-tab-content" style={{ flexDirection: 'column', alignItems: 'stretch', padding: '6px 10px', gap: 4, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 1, background: '#111', borderRadius: 4, padding: 1 }}>
          <button onClick={() => { if (isPlaying) stop(); setSynthMode('static'); }}
            style={{ background: synthMode === 'static' ? '#333' : 'transparent', border: 'none', borderRadius: 3, padding: '2px 6px', color: synthMode === 'static' ? '#ddd' : '#666', fontSize: 9, cursor: 'pointer' }}>
            Note
          </button>
          <button onClick={() => { if (isPlaying) stop(); setSynthMode('follow'); }}
            style={{ background: synthMode === 'follow' ? '#333' : 'transparent', border: 'none', borderRadius: 3, padding: '2px 6px', color: synthMode === 'follow' ? '#ddd' : '#666', fontSize: 9, cursor: 'pointer' }}>
            Follow
          </button>
        </div>
        <button onClick={isPlaying ? stop : play} style={{
          background: isPlaying ? '#f87171' : '#22c55e', border: 'none', borderRadius: 4,
          padding: '2px 8px', color: '#000', fontSize: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 3,
        }}>
          {isPlaying ? <><Square size={9} /> Stop</> : <><Play size={9} /> Synth</>}
        </button>
        {isEdited && (
          <button onClick={reset} style={{
            background: 'transparent', border: '1px solid #555', borderRadius: 4,
            padding: '2px 6px', color: '#a1a1aa', fontSize: 9, cursor: 'pointer',
          }}>Reset</button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, flex: 1, minHeight: 60, overflow: 'hidden' }}>
        {(dp || []).map((p, i) => {
          const relDb = p.db - maxDb;
          const h = Math.max(2, ((relDb + dbRange) / dbRange) * 100);
          const canEdit = synthMode === 'static';
          return (
            <div key={p.n} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'flex-end', height: '100%', cursor: canEdit ? 'ns-resize' : 'default' }}
              onMouseDown={canEdit ? (e) => onBarMouseDown(e, i) : undefined}
              onDoubleClick={canEdit ? (e) => { e.stopPropagation(); setModalH(i); } : undefined}>
              <div style={{
                width: '100%', maxWidth: 16, height: `${h}%`,
                background: harmonicColor(p.n, 0.8),
                borderRadius: '3px 3px 0 0',
                transition: synthMode === 'follow' ? 'height 0.05s' : 'none',
              }} />
              <span style={{ fontSize: 7, color: '#71717a', marginTop: 1 }}>H{p.n}</span>
              <span style={{ fontSize: 6, color: '#52525b' }}>{relDb.toFixed(0)}</span>
            </div>
          );
        })}
      </div>

      {modalH !== null && (
        <HarmonicEditorModal
          profile={profile}
          maxDb={maxDb}
          onUpdate={updateHarmonic}
          onClose={() => setModalH(null)}
          isPlaying={isPlaying}
          onPlay={play}
          onStop={stop}
        />
      )}
    </div>
  );
}

// ── Full-screen harmonic spectrum editor ──────────────────

function HarmonicEditorModal({ profile, maxDb, onUpdate, onClose, isPlaying, onPlay, onStop }: {
  profile: EditableHarmonic[];
  maxDb: number;
  onUpdate: (idx: number, updates: Partial<EditableHarmonic>) => void;
  onClose: () => void;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalDragRef = useRef<{ idx: number; field: 'pos' | 'bw'; startX: number; startY: number; startFreq: number; startDb: number; startBw: number } | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  const maxFreq = 6000;
  const dbMin = maxDb - 80;
  const padL = 45, padR = 15, padT = 25, padB = 30;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    const W = rect.width, H = rect.height;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    const freqToX = (f: number) => padL + (f / maxFreq) * plotW;
    const dbToY = (db: number) => padT + plotH * (1 - (db - dbMin) / (maxDb - dbMin));
    const baseline = padT + plotH;

    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 0.5;
    for (let db = Math.ceil(dbMin / 10) * 10; db <= maxDb; db += 10) {
      const y = dbToY(db);
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = '#555'; ctx.font = '9px monospace'; ctx.textAlign = 'right';
      ctx.fillText(`${db}`, padL - 4, y + 3);
    }
    for (let f = 500; f < maxFreq; f += 500) {
      const x = freqToX(f);
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, baseline); ctx.stroke();
      ctx.fillStyle = '#555'; ctx.font = '9px monospace'; ctx.textAlign = 'center';
      ctx.fillText(f >= 1000 ? `${f / 1000}k` : `${f}`, x, baseline + 14);
    }

    // Draw each harmonic as a bell curve (Gaussian shape based on bandwidth)
    for (let i = 0; i < profile.length; i++) {
      const p = profile[i];
      const cx = freqToX(p.freq);
      const cy = dbToY(p.db);
      const isHov = hovered === i;
      const color = harmonicColor(p.n);

      // Gaussian bell curve: sigma in Hz = bw / 2.35 (FWHM to sigma)
      const sigmaHz = Math.max(5, p.bw / 2.35);
      const sigmaPixels = (sigmaHz / maxFreq) * plotW;
      const extent = sigmaPixels * 3; // draw ±3 sigma

      // Filled bell curve
      ctx.beginPath();
      ctx.moveTo(cx - extent, baseline);
      const steps = Math.max(20, Math.round(extent * 2 / 2));
      for (let s = 0; s <= steps; s++) {
        const px = cx - extent + (s / steps) * extent * 2;
        const dist = px - cx;
        const gaussian = Math.exp(-0.5 * (dist / sigmaPixels) ** 2);
        const py = baseline - (baseline - cy) * gaussian;
        ctx.lineTo(px, py);
      }
      ctx.lineTo(cx + extent, baseline);
      ctx.closePath();
      ctx.fillStyle = harmonicColor(p.n, isHov ? 0.4 : 0.2);
      ctx.fill();

      // Bell curve outline
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const px = cx - extent + (s / steps) * extent * 2;
        const dist = px - cx;
        const gaussian = Math.exp(-0.5 * (dist / sigmaPixels) ** 2);
        const py = baseline - (baseline - cy) * gaussian;
        if (s === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = isHov ? 2 : 1;
      ctx.globalAlpha = isHov ? 1 : 0.7;
      ctx.stroke();

      // Peak dot
      ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.arc(cx, cy, isHov ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = color; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = isHov ? 2 : 1; ctx.stroke();

      // Bandwidth drag handles (at -6dB points = ±bw/2)
      const bwHalfPx = (p.bw / 2 / maxFreq) * plotW;
      const handleY = baseline - (baseline - cy) * 0.5; // -6dB point
      ctx.beginPath(); ctx.arc(cx - bwHalfPx, handleY, 3, 0, Math.PI * 2);
      ctx.fillStyle = isHov ? '#fff' : color; ctx.globalAlpha = 0.8; ctx.fill();
      ctx.beginPath(); ctx.arc(cx + bwHalfPx, handleY, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // Label
      ctx.fillStyle = isHov ? '#fff' : color;
      ctx.font = isHov ? 'bold 11px monospace' : '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`H${p.n}`, cx, cy - 10);

      if (isHov) {
        ctx.fillStyle = '#fff'; ctx.font = '9px monospace';
        ctx.fillText(`${Math.round(p.freq)}Hz  ${Math.round(p.db)}dB  bw:${Math.round(p.bw)}Hz`, cx, cy - 22);
      }
    }
    ctx.globalAlpha = 1;
  }, [profile, maxDb, dbMin, hovered]);

  useEffect(() => { draw(); }, [draw]);

  const hitTest = (e: React.MouseEvent): { idx: number; field: 'pos' | 'bw' } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const plotW = rect.width - padL - padR;
    const plotH = rect.height - padT - padB;

    for (let i = 0; i < profile.length; i++) {
      const p = profile[i];
      const x = padL + (p.freq / maxFreq) * plotW;
      const y = padT + plotH * (1 - (p.db - dbMin) / (maxDb - dbMin));
      const bwHalf = (p.bw / 2 / maxFreq) * plotW;
      const handleY = padT + plotH * (1 - (p.db - dbMin) / (maxDb - dbMin));
      const halfPtY = y + (padT + plotH - y) * 0.5; // -6dB point
      if (Math.abs(mx - (x - bwHalf)) < 8 && Math.abs(my - halfPtY) < 10) return { idx: i, field: 'bw' };
      if (Math.abs(mx - (x + bwHalf)) < 8 && Math.abs(my - halfPtY) < 10) return { idx: i, field: 'bw' };
      if (Math.abs(mx - x) < 10 && Math.abs(my - y) < 10) return { idx: i, field: 'pos' };
    }
    return null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const hit = hitTest(e);
    if (!hit) return;
    e.preventDefault();
    const p = profile[hit.idx];
    modalDragRef.current = { idx: hit.idx, field: hit.field, startX: e.clientX, startY: e.clientY, startFreq: p.freq, startDb: p.db, startBw: p.bw };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!modalDragRef.current) {
      const hit = hitTest(e);
      setHovered(hit ? hit.idx : null);
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = hit ? (hit.field === 'bw' ? 'ew-resize' : 'move') : 'default';
      return;
    }
    const d = modalDragRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const plotW = rect.width - padL - padR;
    const plotH = rect.height - padT - padB;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;

    if (d.field === 'pos') {
      const newFreq = Math.max(20, Math.min(maxFreq, d.startFreq + (dx / plotW) * maxFreq));
      const newDb = Math.max(dbMin, Math.min(maxDb, d.startDb - (dy / plotH) * (maxDb - dbMin)));
      onUpdate(d.idx, { freq: newFreq, db: newDb });
    } else {
      const newBw = Math.max(5, d.startBw + (dx / plotW) * maxFreq * 2);
      onUpdate(d.idx, { bw: newBw });
    }
  };

  const handleMouseUp = () => { modalDragRef.current = null; };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}>
      <div style={{ background: '#0d0d1a', borderRadius: 12, border: '1px solid #333', width: '80vw', maxWidth: 900, height: '60vh', maxHeight: 500, display: 'flex', flexDirection: 'column', padding: 12 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#71717a' }}>Drag peaks: freq/amplitude · Drag side handles: bandwidth</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={isPlaying ? onStop : onPlay} style={{
              background: isPlaying ? '#f87171' : '#22c55e', border: 'none', borderRadius: 4,
              padding: '4px 12px', color: '#000', fontSize: 11, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {isPlaying ? <><Square size={10} /> Stop</> : <><Play size={10} /> Play</>}
            </button>
            <button onClick={onClose} style={{ background: '#333', border: 'none', borderRadius: 4, padding: '4px 12px', color: '#aaa', fontSize: 11, cursor: 'pointer' }}>Done</button>
          </div>
        </div>
        <canvas ref={canvasRef} style={{ flex: 1, width: '100%' }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        />
      </div>
    </div>
  );
}
